import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useSnackbar } from '@context/SnackbarContext'

import { Button } from '@ui/components/Button'
import { Input } from '@ui/components/Input'

import { NameDto } from '@model/NameDto'

import { SetState } from '@global/types/SetState'

import { Box } from '@shared/components/Box'

import { CUSTOM_LANDING_PAGE_OPTION_ID } from './ReferralLink'

const CUSTOM_URL_REGEX =
  /^((https?):\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[a-zA-Z0-9\-._~!?$%&'()*+,;=:@/]*)?$/

interface ReferralCustomTargetUrlProps {
  landingPageUrl: NameDto<string> | undefined
  customTargetUrl: string | null
  setCustomLandingPageUrl: SetState<string | null>
  isLoadingLpUrlsFromCategories: boolean
  isShortLinkLoading: boolean
  isLinkHub?: boolean
  setLandingPageUrl: (url: NameDto<string>) => void
}

export const ReferralCustomTargetUrl = ({
  landingPageUrl,
  customTargetUrl,
  setCustomLandingPageUrl,
  isLoadingLpUrlsFromCategories,
  isShortLinkLoading,
  isLinkHub,
  setLandingPageUrl,
}: ReferralCustomTargetUrlProps) => {
  const { addSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const [inputValue, setInputValue] = useState('')

  const isCustomTargetUrlValid = useCallback(
    (value: string) => {
      const currentTargetUrl = value
      const previousTargetUrl = customTargetUrl

      if (!currentTargetUrl) {
        addSnackbar.info({ message: t('Validation.Please, enter the link to generate') })
        setInputValue('')

        return false
      } else if (previousTargetUrl === currentTargetUrl) {
        addSnackbar.info({ message: t('Validation.Please, enter different target URL') })

        return false
      }

      if (currentTargetUrl && !CUSTOM_URL_REGEX.test(currentTargetUrl)) {
        addSnackbar.error({ message: t('Validation.Please, enter a valid URL') })
        return false
      }

      return true
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customTargetUrl, t]
  )

  return (
    <>
      {(isLinkHub || landingPageUrl?.id === CUSTOM_LANDING_PAGE_OPTION_ID) && (
        <Box flex gap='x8' direction={isLinkHub ? 'column' : 'row'}>
          <Input
            label={t('Referral Materials.Custom target URL')}
            fullWidth
            disabled={isLoadingLpUrlsFromCategories}
            size='lg'
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
          />
          <Button.Secondary
            size='md'
            loading={isShortLinkLoading}
            disabled={!inputValue}
            onClick={() => {
              const value = inputValue.trim() ?? ''

              if (!isCustomTargetUrlValid(value)) {
                return
              }

              if (isLinkHub) {
                setLandingPageUrl({ id: CUSTOM_LANDING_PAGE_OPTION_ID, name: t('Custom') })
              }

              setCustomLandingPageUrl(value || null)
            }}
          >
            {t('Referral Materials.Generate')}
          </Button.Secondary>
        </Box>
      )}
    </>
  )
}
